"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { GoPlus } from "react-icons/go";
import { LuMinus } from "react-icons/lu";

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  title?: string;
  items: FAQItem[];
  className?: string;
}

export default function FAQSection({
  title = "Frequently Asked Questions",
  items,
  className = "",
}: FAQSectionProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className={`relative w-full px-4 py-8 md:p-0 ${className}`}>
      <div className="mx-auto">
        {/* Red gradient background container */}
        <div className="from-brand-red to-brand-dark-red bg-linear-to-r p-5 md:px-[100px] md:py-10">
          {/* Title */}
          <h2 className="font-raleway mb-8 text-center text-[24px] font-bold text-white md:mb-10 md:text-[32px]">
            {title}
          </h2>

          {/* FAQ Items */}
          <div className="space-y-0">
            {items.map((item, index) => (
              <div key={index} className="border-b border-white/30">
                {/* Question row */}
                <button
                  className="flex w-full cursor-pointer items-center justify-between py-5 text-left md:py-10"
                  onClick={() => toggleItem(index)}
                >
                  <span className="font-raleway pr-4 text-[16px] font-bold text-white md:text-[20px]">
                    {index + 1}. {item.question}
                  </span>
                  <div className="shrink-0">
                    {expandedIndex === index ? (
                      <LuMinus className="h-6 w-6 text-white" />
                    ) : (
                      <GoPlus className="h-6 w-6 text-white" />
                    )}
                  </div>
                </button>

                {/* Answer - Animated */}
                <AnimatePresence>
                  {expandedIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <motion.p
                        initial={{ y: -10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -10, opacity: 0 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                        className="pb-5 text-[14px] leading-relaxed font-semibold text-white/90 md:pb-6 md:text-[16px]"
                      >
                        {item.answer}
                      </motion.p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
